<template>
    <div>
        <!-- Reorderable mode with custom row structure -->
        <div v-if="reorderable">
            <div class="table table-sm mb-0 hide-header">
                <Draggable v-model="localValue" 
                          :disabled="editingRowIndex >= 0"
                          handle=".drag-handle"
                          @end="onDragEnd"
                          tag="div"
                          class="table-body">
                    <div v-for="(item, index) in localValue" :key="index" 
                         class="table-row d-flex align-items-center">
                        <!-- Handle column -->
                        <div class="table-cell drag-handle-column">
                            <div class="drag-handle" :class="{ 'text-muted': editingRowIndex >= 0 }" style="cursor: move;">
                                <i class="fas fa-grip-vertical"></i>
                            </div>
                        </div>
                        
                        <!-- Name column -->
                        <div class="table-cell flex-fill">
                            <b-input v-model="editingRowData.name" v-if="editingRowIndex === index" autofocus maxlength="64" />
                            <span v-else>{{ item.name }}</span>
                        </div>
                        
                        <!-- Status column -->
                        <div class="table-cell status-column">
                            <b-select v-model="editingRowData.status" v-if="editingRowIndex === index" :options="statusOptions" />
                            <template v-else>
                                <strong v-if="item.status">
                                    {{statusOptions.find(so => so.value === item.status)?.text}}
                                </strong>
                                <small v-else class="text-muted">
                                    <em>Any Status</em>
                                </small>
                            </template>
                        </div>
                        
                        <!-- Actions column -->
                        <div class="table-cell actions">
                            <div class="d-flex" style="gap: 6px;">
                                <template v-if="editingRowIndex !== index">
                                    <b-button variant="link" @click.prevent="startTableEdit(item, index)" title="Edit">
                                        <b-icon icon="pencil" variant="primary" />
                                    </b-button>
                                    <b-button v-if="item.id !== 0 && !item.id" variant="link"
                                        @click.prevent="removeTableItem(index)" title="Remove">
                                        <b-icon icon="x-circle-fill" variant="danger" />
                                    </b-button>
                                </template>
                                <template v-else>
                                    <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)">
                                        <b-icon icon="save" variant="primary" />
                                    </b-button>
                                    <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                                        <b-icon icon="x-lg" variant="" />
                                    </b-button>
                                </template>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
        </div>
        
        <!-- Non-reorderable mode with b-table -->
        <div v-else>
            <b-table :items="tableItems" :fields="tableFields"
                class="mb-0 hide-header" small bordered>
                <template #cell(value)="{ item, index }">
                    <b-input v-model="editingRowData.name" v-if="editingRowIndex === index" autofocus maxlength="64" />
                    <template v-else>{{ item.value.name }}</template>
                </template>
                <template #cell(status)="{ item, index }">
                    <b-select v-model="editingRowData.status" v-if="editingRowIndex === index" :options="statusOptions" />
                    <template v-else>
                        <strong v-if="item.value.status">
                            {{statusOptions.find(so => so.value === item.value.status)?.text}}
                        </strong>
                        <small v-else class="text-muted">
                            <em>Any Status</em>
                        </small>
                    </template>
                </template>
                <template #cell(actions)="{ item, index }">
                    <div class="d-flex" style="gap: 6px;">
                        <template v-if="editingRowIndex !== index">
                            <b-button variant="link" @click.prevent="startTableEdit(item.value, index)" title="Edit">
                                <b-icon icon="pencil" variant="primary" />
                            </b-button>
                            <b-button v-if="item.value.id !== 0 && !item.value.id" variant="link"
                                @click.prevent="removeTableItem(index)" title="Remove">
                                <b-icon icon="x-circle-fill" variant="danger" />
                            </b-button>
                        </template>
                        <template v-else>
                            <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)">
                                <b-icon icon="save" variant="primary" />
                            </b-button>
                            <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                                <b-icon icon="x-lg" variant="" />
                            </b-button>
                        </template>
                    </div>
                </template>
            </b-table>
        </div>

        <b-button size="sm" variant="primary" @click="addTableItem">
            {{ "Add Reason" }}
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import Draggable from 'vuedraggable';

interface PriorAuthReasonItem {
    id?: number;
    name: string;
    status?: number;
    active?: boolean;
}

interface StatusOption {
    value?: number;
    text: string;
}

@Component({
    name: "PriorAuthReasonsField",
    components: {
        Draggable
    }
})
export default class PriorAuthReasonsField extends Vue {
    @Prop({ default: () => [] }) value!: PriorAuthReasonItem[];
    @Prop({ type: Boolean, default: false }) reorderable!: boolean;

    editingRowData: PriorAuthReasonItem = { name: "", active: true };
    editingRowIndex: number = -1;
    localValue: PriorAuthReasonItem[] = [];

    @Watch('value', { immediate: true, deep: true })
    onValueChange(newValue: PriorAuthReasonItem[]) {
        this.localValue = [...newValue];
    }

    get tableItems() {
        return this.localValue.map((ir, ix) => ({ index: ix, value: ir }));
    }

    get statusOptions(): StatusOption[] {
        return [
            { value: undefined, text: 'Any Status' },
            { value: 5, text: 'Denied' },
            { value: 2, text: 'Abandoned' },
            { value: 6, text: 'Cancelled' }
        ];
    }

    get tableFields() {
        const fields: any[] = [];
        
        // Only include handle column for b-table in non-reorderable mode
        // In reorderable mode, we use custom div structure
        
        fields.push(
            { key: 'value', label: 'Name' },
            { key: 'status', label: 'Limit to Status', class: 'status' },
            { key: 'actions', label: '', class: 'actions' }
        );
        
        return fields;
    }

    onDragEnd() {
        this.$emit('input', [...this.localValue]);
    }

    startTableEdit(item: PriorAuthReasonItem, index: number) {
        this.editingRowIndex = index;
        this.editingRowData = { ...item };
    }

    addTableItem() {
        const cleanedArray = this.localValue.filter((a) => a.name.trim());
        cleanedArray.push({ name: "", active: true });
        this.localValue = cleanedArray;
        this.$emit('input', [...this.localValue]);
        this.startTableEdit({ name: "", active: true }, cleanedArray.length - 1);
    }

    removeTableItem(index: number) {
        const array = [...this.localValue];
        array.splice(index, 1);
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
    }

    cancelTableEdit() {
        const editingIndex = this.editingRowIndex;
        const editingValue = this.localValue[editingIndex].name;
        if (!editingValue) {
            this.removeTableItem(editingIndex);
        }
        this.editingRowIndex = -1;
    }

    saveTableEdit(index: number) {
        const array = [...this.localValue];
        if (!this.editingRowData.name) {
            array.splice(index, 1);
        } else {
            array.splice(index, 1, this.editingRowData);
        }
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
        this.editingRowIndex = -1;
    }
}

</script>

<style scoped lang="scss">
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

table ::v-deep(.actions) {
    width: 90px;
}

table ::v-deep(.status) {
    width: 120px;
}

table ::v-deep(.drag-handle-column) {
    width: 30px;
    text-align: center;
}

.drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    
    &.text-muted {
        cursor: not-allowed !important;
        opacity: 0.5;
    }
}

// Custom table styles for reorderable mode
.table {
    &.hide-header .table-body .table-row:first-child {
        // Hide header row effect in custom structure
    }
}

.table-body {
    display: block;
}

.table-row {
    border: 1px solid #dee2e6;
    border-top: 0;
    padding: 1px 7px;
    min-height: 38px;
    
    &:first-child {
        border-top: 1px solid #dee2e6;
    }
    
    &:hover {
        background-color: rgba(0, 0, 0, 0.075);
    }
}

.table-cell {
    display: flex;
    align-items: center;
    
    &.drag-handle-column {
        width: 30px;
        flex-shrink: 0;
        text-align: center;
        padding: 0;
    }
    
    &.actions {
        width: 90px;
        flex-shrink: 0;
        justify-content: flex-end;
        padding-left: 0.5rem;
    }
    
    &.status-column {
        width: 120px;
        flex-shrink: 0;
        padding: 0 0.5rem;
    }
    
    &.flex-fill {
        flex: 1;
        padding-right: 0.5rem;
    }
}
</style>